import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getFieldLabel, formatAuditValue } from "@/lib/audit_field_labels";
import { AlertTriangle } from "lucide-react";

// Sprint CONCORRENZA / PRATICHE-CONCORRENZA / CRM-2C — UNICA finestra dei conflitti (veicolo, pratica e anagrafica).
// Nessuna scelta preselezionata; «Applica» solo quando si è deciso su tutti.

// Sprint CRM-2C.1 — un testo di soli spazi vale come vuoto
const testoVuoto = (x) => typeof x === "string" && x.trim() === "";
const vuoto = (x) => x == null || testoVuoto(x);
const eq = (a, b) => {
  if (vuoto(a) && vuoto(b)) return true;
  if (!testoVuoto(a) && !testoVuoto(b) && !isNaN(Number(a)) && !isNaN(Number(b)) && typeof a !== "boolean" && typeof b !== "boolean"
      && (typeof a === "number" || typeof b === "number")) return Number(a) === Number(b);
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
};

// Confronta ciò che si sta per salvare (`dopo`) con il punto di partenza: solo i campi cambiati,
// ciascuno con il valore che aveva all'apertura. Contano solo le chiavi di `dopo`.
// Sprint CRM-2C.1 — un campo passato da pieno a vuoto (anche di soli spazi) è cambiato: si manda come vuoto (null), mai "".
const inviato = (x) => (x === undefined || testoVuoto(x) ? null : x);
export function campiModificati(prima, dopo) {
  const payload = {}; const originali = {};
  const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);
  for (const k of Object.keys(dopo || {})) {
    const a = prima?.[k]; const b = dopo?.[k];
    if (k !== "deposito" && (isObj(a) || isObj(b))) {
      for (const s of Object.keys(b || {})) {
        if (eq(a?.[s], b?.[s])) continue;
        payload[k] = { ...(payload[k] || {}), [s]: inviato(b?.[s]) };
        originali[`${k}.${s}`] = a?.[s] ?? null;
      }
    } else if (!eq(a, b)) {
      payload[k] = inviato(b);
      originali[k] = a ?? null;
    }
  }
  return { payload, originali };
}

// «Sovrascrivi» sui campi scelti: valore mio, con il valore salvato ora come partenza.
// `prefisso` (es. "finanziamento.") si toglie per le rotte che salvano una sola voce.
export function sovrascrittura(conflitti, mieiValori, prefisso = "") {
  const payload = {}; const originali = {};
  for (const c of conflitti) {
    const campo = c.campo.startsWith(prefisso) ? c.campo.slice(prefisso.length) : c.campo;
    const mio = valoreDiCampo(mieiValori, c.campo);
    const [k, sub] = campo.split(".");
    if (sub) payload[k] = { ...(payload[k] || {}), [sub]: mio ?? null };
    else payload[k] = mio ?? null;
    originali[campo] = c.valore_attuale ?? null;
  }
  return { payload, originali };
}

const VOCI = { finanziamento: "Finanziamento", permuta: "Permuta", pagamenti_caparra: "Caparra", passaggio_proprieta: "Passaggio di proprietà" };
const etichetta = (campo) => {
  const l = getFieldLabel(campo);
  const [voce, ...resto] = campo.split(".");
  if (l !== campo || !VOCI[voce]) return l;
  return `${VOCI[voce]} — ${getFieldLabel(resto.join("."))}`;
};
const mostra = (v, campo) => (typeof v === "number"
  ? v.toLocaleString("it-IT", { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 })
  : formatAuditValue(v, campo));

export const valoreDiCampo = (obj, campo) => {
  const [k, s] = campo.split(".");
  return s ? obj?.[k]?.[s] : obj?.[k];
};

const quando = (iso) => {
  if (!iso) return "data non nota";
  const d = new Date(iso);
  return `il ${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} alle ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
};

export default function Conflitti({ esito, mieiValori, onApplica, onChiudi }) {
  const [scelte, setScelte] = useState({});
  if (!esito) return null;
  const { conflitti, campi_salvati: salvati } = esito;
  const tutteDecise = conflitti.every((c) => scelte[c.campo]);
  const scegli = (campo, v) => setScelte((s) => ({ ...s, [campo]: v }));
  const tieniTutti = () => setScelte(Object.fromEntries(conflitti.map((c) => [c.campo, "attuale"])));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="max-w-2xl" data-testid="conflitti-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" />Modifiche di qualcun altro</DialogTitle>
          <DialogDescription data-testid="conflitti-riepilogo">
            Salvat{salvati === 1 ? "o" : "i"} {salvati} camp{salvati === 1 ? "o" : "i"}. {conflitti.length} camp{conflitti.length === 1 ? "o è stato modificato" : "i sono stati modificati"} da qualcun altro mentre lavoravi: scegli cosa tenere.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-auto">
          {conflitti.map((c) => (
            <div key={c.campo} className="border border-slate-200 rounded p-3" data-testid={`conflitto-${c.campo}`}>
              <div className="font-medium text-sm text-slate-800">{etichetta(c.campo)}</div>
              <div className="text-xs text-slate-500 mb-2" data-testid={`conflitto-autore-${c.campo}`}>
                {c.autore_registrato
                  ? `Modificato da ${c.modificato_da || "utente sconosciuto"} ${quando(c.modificato_il)}`
                  : `Modificato ${quando(c.modificato_il)} da un'altra operazione, autore non registrato`}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SceltaValore attiva={scelte[c.campo] === "mio"} onClick={() => scegli(c.campo, "mio")}
                  titolo="Il tuo valore (sovrascrivi)" valore={mostra(valoreDiCampo(mieiValori, c.campo), c.campo)}
                  testid={`conflitto-mio-${c.campo}`} />
                <SceltaValore attiva={scelte[c.campo] === "attuale"} onClick={() => scegli(c.campo, "attuale")}
                  titolo="Valore salvato ora (tieni)" valore={mostra(c.valore_attuale, c.campo)}
                  testid={`conflitto-attuale-${c.campo}`} />
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="outline" onClick={tieniTutti} data-testid="conflitti-tieni-tutti">Tieni tutti i valori attuali</Button>
          <Button disabled={!tutteDecise} onClick={() => onApplica(scelte)} data-testid="conflitti-applica">Applica</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SceltaValore({ attiva, onClick, titolo, valore, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left rounded border p-2 transition-colors ${attiva ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{titolo}</div>
      <div className="text-sm text-slate-900 break-words">{valore === "" || valore == null ? "—" : String(valore)}</div>
    </button>
  );
}
